import BaseComponent from '@js/BaseComponent'
import { hasMotion, onHasMotion, hasTouch } from '@js/mediaQueries'

/**
 * Manages AmbientVideo component instances.
 *
 * This class controls the playback state of the video as well as the states of the button controlling it. It intelligently pauses playback on window blur and as videos come into and out of view.
 *
 * @extends BaseComponent
 */
class AmbientVideo extends BaseComponent {
    autoplay = false

    constructor(element) {
        super(element)

        this.mutationObserver = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
                if (mutation.type === 'attributes') {
                    if (this.playbackController && this.playbackController.dataset.state !== 'loading') {
                        this.playbackController.disabled = false
                        this.mutationObserver.disconnect()
                    }
                }
            })
        })

        this.userPause = undefined

        if (this.element.hasAttribute('autoplay')) {
            this.autoplay = true
        }

        this.element.removeAttribute('autoplay')

        if (!('useNativeControls' in this.element.dataset)) {
            this.element.removeAttribute('controls')
        }

        this.playbackController = document.querySelector(`[aria-controls="${this.element.id}"][data-control-type="playback"]`)
        this.audioController = document.querySelector(`[aria-controls="${this.element.id}"][data-control-type="audio"]`)

        if (this.playbackController) {
            this.playbackController.disabled = true
            this.mutationObserver.observe(this.playbackController, {
                attributes: true
            })
        }

        if (this.playbackController || this.audioController) {
            this.addEventListeners()
        }

        if (this.element.muted && this.audioController) {
            this.audioController.dataset.state = 'unmute'
            this.audioController.ariaLabel = this.audioController.dataset.labelStateUnmute
        }

        onHasMotion(hasMotion => {
            if (!hasMotion) {
                this.element.pause()
            } else if (this.element.paused && this.userPause === false) {
                this.element.play()
            } else if (this.element.paused) {
                this.playbackController?.classList.add('ambient-video-control-showplay')
            }
        })
    }

    /**
     * Adds event listeners for the element, the controller, and the document
     */
    addEventListeners() {

        this.playbackController?.addEventListener('click', () => {
            switch (this.playbackController.dataset.state) {
                case 'play':
                    this.userPause = false
                    this.element.play()
                    break;
                case 'pause':
                    this.userPause = true
                    this.element.pause()
                    break;
                case 'replay':
                    this.userPause = false
                    this.element.currentTime = 0
                    this.element.play()
                    break;
            }
        })

        this.audioController?.addEventListener('click', () => {
            switch (this.audioController.dataset.state) {
                case 'mute':
                    this.element.muted = true
                    break;
                case 'unmute':
                    this.element.muted = false
                    break;
            }
        })

        Array('focus', 'mouseenter').forEach(event => {
            this.playbackController?.addEventListener(event, () => {
                this.onControlActive()
            })

            this.audioController?.addEventListener(event, () => {
                this.onControlActive()
            })
        })

        Array('blur', 'mouseleave').forEach(event => {
            this.playbackController?.addEventListener(event, () => {
                this.onControlInactive()
            })

            this.audioController?.addEventListener(event, () => {
                this.onControlInactive()
            })
        })

        this.element.addEventListener('canplay', () => {
            if (this.playbackController && !this.autoplay && !this.userPause) {
                this.playbackController.dataset.state = 'play'
                this.playbackController.ariaLabel = this.playbackController.dataset.labelStatePlay
            }
        })

        this.element.addEventListener('pause', () => {
            if (this.playbackController) {
                this.playbackController.dataset.state = 'play'
                this.playbackController.ariaLabel = this.playbackController.dataset.labelStatePlay
            }
        })

        this.element.addEventListener('play', () => {
            const censoredWrapper = this.element.closest('.censored')
            if (censoredWrapper) {
                sir.components[censoredWrapper.dataset.componentid]?.reveal()
            }

            if (this.playbackController) {
                this.playbackController.dataset.state = 'pause'
                this.playbackController.classList.remove('ambient-video-control-showplay')
                this.playbackController.ariaLabel = this.playbackController.dataset.labelStatePause
            }
        })

        this.element.addEventListener('ended', () => {
            if (this.playbackController) {
                this.playbackController.dataset.state = 'replay'
                this.playbackController.ariaLabel = this.playbackController.dataset.labelStateReplay
            }
        })

        this.element.addEventListener('volumechange', () => {
            if (this.audioController) {
                if (this.element.muted) {
                    this.audioController.dataset.state = 'unmute'
                    this.audioController.ariaLabel = this.audioController.dataset.labelStateUnmute
                } else {
                    this.audioController.dataset.state = 'mute'
                    this.audioController.ariaLabel = this.audioController.dataset.labelStateMute
                }
            }
        })

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden' && !this.element.paused) {
                this.element.pause()
            }

            if (
                document.visibilityState === 'visible' &&
                this.userPause === false &&
                !this.element.ended &&
                hasMotion()
            ) {
                this.element.play()
            }
        })
    }

    onControlActive() {
        if (hasTouch()) return

        this.element.classList.add(this.focusClass)
        this.element.dataset.focusRing = 'active'
        const highlightColor = window.getComputedStyle(this.element).getPropertyValue('--highlight')
        this.element.style.setProperty('--highlight', highlightColor)
    }

    onControlInactive() {
        if (hasTouch()) return

        this.element.dataset.focusRing = null
    }

    get userPause() {
        if (this.element.dataset.userPause === 'true') {
            return true
        } else if (this.element.dataset.userPause === 'false') {
            return false
        }

        return undefined
    }

    set userPause(newValue) {
        this.element.dataset.userPause = newValue
    }
}

/**
 * Registers a single IntersectionObserver for each video on the page after all have been loaded
 * @param {AmbientVideo[]} videos - All AmbientVideo instances on the page
 */
AmbientVideo.onAfterCreate = (videos) => {
    const options = {
        rootMargin: '0px',
        threshold: [0, 0.25],
    }

    const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            const {
                isIntersecting,
                intersectionRatio,
                target: video
            } = entry

            if (video.ended) return

            const videoObj = sir.components[video.dataset.componentid]

            if (
                isIntersecting &&
                intersectionRatio >= 0.25 &&
                video.paused &&
                !!videoObj.userPause === false &&
                hasMotion()
            ) {
                video.play()
            }

            if (!isIntersecting && intersectionRatio <= 0) {
                video.pause()
            }
        })
    }, options)

    videos.forEach(video => {
        if (video.autoplay) {
            observer.observe(video.element)
        }
    })
}

AmbientVideo.create('.ambient-video')
